import React, {
  useEffect,
  useMemo,
} from 'react';
import {
  useFormContext,
} from 'react-hook-form';
import isNil from 'lodash/isNil';
import range from 'lodash/range';
import Controller from 'core/components/FormController';
import Accordion from 'core/components/Accordion';
import DatePicker from 'core/components/DateTimePicker/SingleDatePicker';
import Input from 'pages/Dashboard/components/Input';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import Select from 'core/components/Select';
import {
  validateDate,
  validatePatientName,
  validateSSN,
} from 'pages/Dashboard/utils/helper';
import MaskedTextField from 'core/components/MaskedTextField';
import {
  IMask,
} from 'react-imask';
import {
  isEmptyString,
} from '@populateui/utils';
import HeightInput from 'pages/Dashboard/pages/Charts/pages/Details/components/Demographics/HeightInput';
import WeightInput from 'pages/Dashboard/pages/Charts/pages/Details/components/Demographics/WeightInput';
import SSNInput from 'pages/Dashboard/pages/Charts/pages/Details/components/Demographics/SSNInput';
import SwitchButton from 'core/components/Switch';
import ToolTip from 'core/components/Tooltip';
import Divider from '@mui/material/Divider';

const genders = ['Male', 'Female', 'Other'];

const shoeSizeRange: string[] = range(1, 21).map((num) => num.toString());

const shoeSizeMask = [{
  mask: 'digit.fraction',
  overwrite: true,
  eager: true,
  blocks: {
    digit: {
      mask: IMask.MaskedEnum,
      enum: shoeSizeRange,
    },
    fraction: {
      mask: IMask.MaskedEnum,
      enum: ['0', '5'],
    },
  },
}];

function normalizeShoeSizeValue(value: string) {
  let normalizedValue = value;

  if (!isEmptyString(value)) {
    const numericValue = Number(value);
    normalizedValue = Number.isFinite(numericValue)
      ? numericValue.toFixed(1).toString()
      : '';
  }

  return normalizedValue;
}

export default function PatientGeneralInfo() {
  const { control, watch, setValue } = useFormContext();

  const [
    isDeceased,
    isInactive,
  ] = watch(['isDeceased', 'isInactive']);

  const turnOffAcceptsCommunication = () => {
    setValue('acceptsCommunication', false, {
      shouldTouch: true,
      shouldDirty: true,
    });
  };

  const isDeceasedOrInactive = useMemo(
    () => isDeceased || isInactive,
    [isDeceased, isInactive],
  );

  useEffect(() => {
    if (isDeceasedOrInactive) {
      turnOffAcceptsCommunication();
    }
  }, [isDeceasedOrInactive]);

  return (
    <Accordion
      defaultExpanded
      header="General Information"
      disableGutters
      headerClasses={{
        root: '!px-2 !py-1 !min-h-[initial] !bg-gray-100 !text-gray-600 !font-semibold text-sm',
        content: '!m-0',
      }}
      detailsClasses={{ root: '!p-2 populate-form gap-form-row flex flex-col w-full' }}
    >
      <div className="flex w-full gap-form-field items-center">
        <Controller
          name="firstName"
          control={control}
          rules={{
            validate: (value) => validatePatientName(value, 'Enter a valid first name'),
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              required
              error={!isNil(error)}
              className="w-1/3"
              label="First Name"
              placeholder="First Name"
              id="firstName"
              inputProps={{ autoComplete: 'off' }}
            />
          )}
        />
        <Controller
          name="middleName"
          control={control}
          rules={{
            validate: (value) => validatePatientName(value, 'Enter a valid middle name', false),
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              error={!isNil(error)}
              label="Middle Name"
              className="w-1/3"
              placeholder="Middle Name"
              id="middleName"
              inputProps={{ autoComplete: 'off' }}
            />
          )}
        />
        <Controller
          name="lastName"
          control={control}
          rules={{
            validate: (value) => validatePatientName(value, 'Enter a valid last name'),
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              required
              error={!isNil(error)}
              className="w-1/3"
              label="Last Name"
              placeholder="Last Name"
              id="lastName"
              inputProps={{ autoComplete: 'off' }}
            />
          )}
        />
      </div>
      <div className="flex w-full items-center gap-form-field">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <Controller
            name="dateOfBirth"
            rules={{
              required: true,
              validate: validateDate,
            }}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Wrapper
                required
                label="Date of Birth"
                id="dob"
                showErrorText={false}
                error={!isNil(error)}
                className="min-w-0 flex-[1.4]"
                labelClasses="whitespace-nowrap"
              >
                <DatePicker
                  {...field}
                  className="!rounded font-medium w-full"
                  showDateButtons={false}
                  containerClassName="!w-full"
                  inputContainerClassName="!w-full"
                  disableFuture
                />
              </Wrapper>
            )}
          />
          <Controller
            name="genderDescription"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Select<string>
                {...field}
                required
                id="gender-option"
                className="min-w-0 flex-1"
                label="Gender"
                placeholder="Gender"
                options={genders}
                getLabel={(option: string) => option}
                getValue={(option: string) => option}
              />
            )}
          />
        </div>
        <div className="flex min-w-0 flex-1 items-end gap-form-field">
          <HeightInput />
        </div>
        <div className="flex min-w-0 flex-1 items-center gap-form-field">
          <WeightInput />
          <Controller
            name="shoeSize"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Wrapper
                id="shoe-size-option"
                className="w-1/2"
                label="Shoe Size"
                showErrorText={false}
                error={!isNil(error)}
              >
                <MaskedTextField
                  {...field}
                  mask={shoeSizeMask}
                  value={field.value?.toString() ?? ''}
                  onAccept={field.onChange}
                  onBlur={(event) => {
                    const normalizedValue = normalizeShoeSizeValue(event.currentTarget.value);
                    field.onChange(normalizedValue);
                    field.onBlur();
                  }}
                  placeholder="e.g 5.0 or 7.5"
                  className="populate-input populate-number-input bg-white"
                />
              </Wrapper>
            )}
          />
        </div>
      </div>
      <div className="flex flex-col gap-form-row w-1/3">
        <Controller
          name="ssn"
          control={control}
          rules={{ validate: validateSSN }}
          render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
            <Wrapper
              id="ssn-option"
              className="w-full flex"
              label="SSN"
              showErrorText={false}
              error={!isNil(error)}
            >
              <SSNInput
                {...field}
                value={value}
                onChange={onChange}
              />
            </Wrapper>
          )}
        />
      </div>
      <Wrapper
        id="patient-status-option"
        className="w-full flex"
        label="Patient Status"
        showErrorText={false}
      >
        <div className="flex gap-form-field items-start w-full">
          <Controller
            name="acceptsCommunication"
            control={control}
            render={({ field }) => (
              <ToolTip
                title="Deceased or inactive patients cannot accept communication."
                hide={!isDeceasedOrInactive}
              >
                <span>
                  <SwitchButton
                    {...field}
                    label="Accepts Communication"
                    checked={field.value}
                    disabled={isDeceasedOrInactive}
                    classes={{
                      rootContainer: '!gap-1',
                      label: '!mt-0.5 !font-medium !text-gray-600',
                    }}
                  />
                </span>
              </ToolTip>
            )}
          />
          <Divider orientation="vertical" flexItem />
          <Controller
            name="isInactive"
            control={control}
            render={({ field }) => (
              <SwitchButton
                {...field}
                label="Chart Inactive"
                checked={field.value ?? false}
                classes={{
                  rootContainer: '!gap-1',
                  label: '!mt-0.5 !font-medium !text-gray-600',
                }}
              />
            )}
          />
          <Divider orientation="vertical" flexItem />
          <Controller
            name="isDeceased"
            control={control}
            render={({ field }) => (
              <SwitchButton
                {...field}
                label="Patient Deceased"
                checked={field.value ?? false}
                classes={{
                  rootContainer: '!gap-1',
                  label: '!mt-0.5 !font-medium !text-gray-600',
                }}
              />
            )}
          />
        </div>
      </Wrapper>
    </Accordion>
  );
}
